import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';

const API = process.env.REACT_APP_API_URL || '';

const POS_COLOR = {
  QB: { bg: 'rgba(var(--accent2-rgb),0.15)', color: 'var(--accent2)', border: 'rgba(var(--accent2-rgb),0.3)' },
  RB: { bg: 'rgba(0,212,170,0.15)', color: '#00d4aa', border: 'rgba(0,212,170,0.3)' },
  WR: { bg: 'rgba(79,195,247,0.15)', color: '#4fc3f7', border: 'rgba(79,195,247,0.3)' },
  TE: { bg: 'rgba(206,147,216,0.15)', color: '#ce93d8', border: 'rgba(206,147,216,0.3)' },
};

const TIER_COLOR = {
  1: '#00ff87', 2: '#4fc3f7', 3: '#ffd32a', 4: '#ff9800', 5: 'var(--text3)',
};

const PRIORITY_STYLE = {
  high:   { bg: 'rgba(255,71,87,0.06)',   border: 'rgba(255,71,87,0.25)',   dot: '#ff4757' },
  medium: { bg: 'rgba(255,211,42,0.05)',  border: 'rgba(255,211,42,0.2)',   dot: '#ffd32a' },
  low:    { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.08)', dot: 'var(--text3)' },
};

const URGENCY_STYLE = {
  high:   { bg: 'rgba(255,71,87,0.12)',  color: '#ff4757' },
  medium: { bg: 'rgba(255,211,42,0.12)', color: '#ffd32a' },
  low:    { bg: 'rgba(255,255,255,0.08)', color: 'var(--text3)' },
};

function PosBadge({ pos, size = 'sm' }) {
  const c = POS_COLOR[pos] || { bg: 'var(--bg3)', color: 'var(--text3)', border: 'var(--border)' };
  const pad = size === 'lg' ? '3px 10px' : '1px 6px';
  const fs = size === 'lg' ? 13 : 10;
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: 4, fontSize: fs, fontWeight: 700, padding: pad,
      letterSpacing: 0.8, flexShrink: 0,
    }}>
      {pos}
    </span>
  );
}

function TierBadge({ tier, label }) {
  const color = TIER_COLOR[tier] || '#666';
  return (
    <span style={{
      color, fontSize: 11, fontWeight: 700,
      background: `${color}18`, border: `1px solid ${color}33`,
      borderRadius: 4, padding: '1px 6px',
    }}>
      T{tier} {label}
    </span>
  );
}

function FaabBadge({ faab }) {
  const s = URGENCY_STYLE[faab.urgency] || URGENCY_STYLE.low;
  return (
    <div style={{
      background: s.bg, color: s.color,
      borderRadius: 6, padding: '4px 10px',
      fontSize: 12, fontWeight: 700, textAlign: 'center', minWidth: 64,
    }}>
      <div>${faab.amount}</div>
      <div style={{ fontSize: 9, fontWeight: 500, opacity: 0.8, marginTop: 1 }}>{faab.label}</div>
    </div>
  );
}

function PlayerChip({ player, action }) {
  const isAdd = action === 'add';
  const accentColor = isAdd ? 'var(--green)' : 'var(--red)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: isAdd ? 'rgba(0,255,135,0.05)' : 'rgba(255,71,87,0.05)',
      border: `1px solid ${isAdd ? 'rgba(0,255,135,0.15)' : 'rgba(255,71,87,0.15)'}`,
      borderRadius: 8, padding: '8px 12px', flex: 1,
    }}>
      <span style={{ color: accentColor, fontWeight: 800, fontSize: 16, lineHeight: 1 }}>
        {isAdd ? '+' : '−'}
      </span>
      <PosBadge pos={player.position} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {player.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ fontSize: 10, color: 'var(--text3)' }}>{player.team || 'FA'}</span>
          {player.age && <span style={{ fontSize: 10, color: 'var(--text3)' }}>· Age {player.age}</span>}
          {player.injury_status && (
            <span style={{ fontSize: 9, background: 'rgba(255,71,87,0.2)', color: 'var(--red)', borderRadius: 3, padding: '0 4px' }}>
              {player.injury_status}
            </span>
          )}
        </div>
      </div>
      <TierBadge tier={player.tier} label={player.tier_label} />
    </div>
  );
}

function RecommendationCard({ rec, index }) {
  const ps = PRIORITY_STYLE[rec.priority] || PRIORITY_STYLE.low;
  return (
    <div style={{
      background: ps.bg, border: `1px solid ${ps.border}`,
      borderRadius: 12, padding: '14px 16px', marginBottom: 10,
      transition: 'border-color 0.2s',
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = rec.priority === 'high' ? 'rgba(255,71,87,0.5)' : 'rgba(255,255,255,0.15)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = ps.border}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Priority indicator */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2, gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: ps.dot, flexShrink: 0 }} />
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700 }}>#{index + 1}</div>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Add / Drop row */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <PlayerChip player={rec.add} action="add" />
            {rec.drop && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text3)', fontSize: 18 }}>⇄</div>
                <PlayerChip player={rec.drop} action="drop" />
              </>
            )}
          </div>

          {/* Reason */}
          <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 8 }}>
            {rec.reason}
          </div>

          {/* Meta row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
              color: rec.priority === 'high' ? 'var(--red)' : rec.priority === 'medium' ? '#ffd32a' : '#555',
              background: ps.bg, border: `1px solid ${ps.border}`, borderRadius: 4, padding: '2px 6px',
            }}>
              {rec.priority} priority
            </span>
            {rec.need_gap > 0 && (
              <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                {rec.need_gap.toFixed(1)} tier gap vs your starters
              </span>
            )}
          </div>
        </div>

        {/* FAAB */}
        <FaabBadge faab={rec.faab} />
      </div>
    </div>
  );
}

function PosGradeRow({ pos, grade }) {
  const color = POS_COLOR[pos]?.color || 'var(--text3)';
  const gradeColor = TIER_COLOR[Math.round(grade.avg_tier || 3)] || 'var(--text3)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <PosBadge pos={pos} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
          <div style={{
            height: '100%', borderRadius: 2,
            width: `${Math.max(5, 100 - ((grade.avg_tier || 5) - 1) * 25)}%`,
            background: gradeColor,
          }} />
        </div>
      </div>
      <span style={{ fontSize: 12, fontWeight: 800, color: gradeColor, minWidth: 24, textAlign: 'right' }}>
        {grade.grade}
      </span>
      <span style={{ fontSize: 10, color: 'var(--text3)', minWidth: 60 }}>
        Tier {grade.avg_tier?.toFixed(1)} avg
      </span>
    </div>
  );
}

export default function Waivers({ api: apiProp }) {
  const apiBase = apiProp || API;
  const { currentUser } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterPos, setFilterPos] = useState('ALL');
  const [filterPriority, setFilterPriority] = useState('ALL');

  const rosterId = currentUser?.roster_id;

  const fetchRecs = useCallback(async () => {
    if (!rosterId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${apiBase}/api/waivers/${rosterId}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, rosterId]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  // Guest or no roster
  if (!currentUser || currentUser.guest || !rosterId) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Waiver Wire</div>
          <div className="page-subtitle">Personalized add/drop recommendations</div>
        </div>
        <div className="empty-state-block">
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <div>Log in with your Sleeper username to see personalized waiver recommendations.</div>
        </div>
      </div>
    );
  }

  const recs = data?.recommendations || [];
  const filtered = recs.filter(r => {
    const posMatch = filterPos === 'ALL' || r.add.position === filterPos;
    const priMatch = filterPriority === 'ALL' || r.priority === filterPriority;
    return posMatch && priMatch;
  });

  const highCount = recs.filter(r => r.priority === 'high').length;
  const posGrades = data?.pos_grades || {};

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Waiver Wire</div>
          <div className="page-subtitle">
            Personalized recommendations for <strong style={{ color: 'var(--text)' }}>{data?.manager || currentUser.display_name}</strong>
            {data?.record && <span style={{ color: 'var(--text3)' }}> · {data.record}</span>}
          </div>
        </div>
        <button className="btn btn-refresh" onClick={fetchRecs} disabled={loading}>
          {loading ? '⏳ Loading...' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.3)', borderRadius: 8, padding: '12px 16px', color: '#ff6b6b', marginBottom: 16 }}>
          ❌ {error}
        </div>
      )}

      {loading && !data && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Scanning available players against dynasty tiers...</span>
        </div>
      )}

      {data && !loading && (
        <div style={{ display: 'grid', gap: 20, alignItems: 'start' }} className="waivers-grid">
          {/* Left: Recommendations */}
          <div>
            {/* Alert banner */}
            {highCount > 0 && (
              <div style={{
                background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 18 }}>🚨</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>
                    {highCount} high-priority waiver target{highCount !== 1 ? 's' : ''} available
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    These players are significantly better than what you currently have at their position.
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {['ALL', 'QB', 'RB', 'WR', 'TE'].map(pos => (
                <button key={pos} onClick={() => setFilterPos(pos)} style={{
                  padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: filterPos === pos ? `1px solid ${POS_COLOR[pos]?.border || 'rgba(255,255,255,0.3)'}` : '1px solid rgba(255,255,255,0.1)',
                  background: filterPos === pos ? (POS_COLOR[pos]?.bg || 'rgba(255,255,255,0.1)') : 'transparent',
                  color: filterPos === pos ? (POS_COLOR[pos]?.color || '#fff') : '#555',
                }}>
                  {pos}
                </button>
              ))}
              <div style={{ width: 1, background: 'rgba(255,255,255,0.08)', margin: '0 4px' }} />
              {['ALL', 'high', 'medium', 'low'].map(pri => (
                <button key={pri} onClick={() => setFilterPriority(pri)} style={{
                  padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: filterPriority === pri ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.08)',
                  background: filterPriority === pri ? 'rgba(255,255,255,0.1)' : 'transparent',
                  color: filterPriority === pri ? '#fff' : '#555',
                }}>
                  {pri === 'ALL' ? 'All Priority' : `${pri.charAt(0).toUpperCase() + pri.slice(1)}`}
                </button>
              ))}
            </div>

            {/* Rec cards */}
            {filtered.length === 0 ? (
              <div className="empty-state-block">
                No recommendations match this filter.
              </div>
            ) : (
              filtered.map((rec, i) => <RecommendationCard key={i} rec={rec} index={i} />)
            )}

            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 12, textAlign: 'center' }}>
              {data.total_available_ranked} dynasty-ranked players scanned on waivers
            </div>
          </div>

          {/* Right: Roster grade sidebar */}
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 12, padding: '16px 16px',
            position: 'sticky', top: 20,
          }}>
            <div className="section-header" style={{ marginBottom: 12 }}>
              <span className="section-header-title">Your Roster Grades</span>
            </div>
            {Object.entries(posGrades).map(([pos, grade]) => (
              <PosGradeRow key={pos} pos={pos} grade={grade} />
            ))}
            <div style={{ marginTop: 16, padding: '10px 12px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>FAAB Guide (per $100)</div>
              {[
                { tier: 'T1 Elite', range: '$40–55', color: '#00ff87' },
                { tier: 'T2 Solid', range: '$18–25', color: '#4fc3f7' },
                { tier: 'T3 Average', range: '$6–10', color: '#ffd32a' },
              ].map(f => (
                <div key={f.tier} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                  <span style={{ color: f.color }}>{f.tier}</span>
                  <span style={{ color: 'var(--text3)' }}>{f.range}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
